import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, LogOut, ExternalLink, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Field, BiField, ImageField } from "./fields";

const TABS = [
  ["general", "General"],
  ["hero", "Hero"],
  ["services", "Servicios"],
  ["gallery", "Galería"],
  ["testimonials", "Testimonios"],
  ["contact", "Contacto"],
];

export default function AdminDashboard({ user, onLogout }) {
  const { setContent } = useSite();
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState("general");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setDraft(r.data));
  }, []);

  const upd = (fn) => setDraft((d) => { const nd = structuredClone(d); fn(nd); return nd; });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft);
      setContent(draft);
      toast.success("Cambios guardados y publicados");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  if (!draft) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <Loader2 className="animate-spin text-volt" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy pb-24 text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-navy-border bg-navy/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <img src={draft.logo || "/assets/logo.png"} alt="" className="h-9 w-auto rounded-md" />
            <div>
              <p className="font-display text-lg uppercase tracking-wider">Panel admin</p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site" className="flex items-center gap-1.5 rounded-lg border border-navy-border px-3 py-2 text-xs font-semibold text-slate-300 hover:border-volt hover:text-volt">
              <ExternalLink size={14} /> <span className="hidden sm:inline">Ver sitio</span>
            </Link>
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-volt px-4 py-2 text-xs font-extrabold uppercase tracking-wide text-navy hover:bg-volt-hover disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="rounded-lg border border-navy-border p-2 text-slate-400 hover:border-red-500/50 hover:text-red-400" aria-label="Salir">
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 pb-2">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                tab === id ? "bg-volt text-navy" : "text-slate-400 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-8">
        {tab === "general" && (
          <div className="space-y-5 rounded-2xl border border-navy-border bg-navy-surface p-6" data-testid="admin-general-panel">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Teléfono (visible)" value={draft.phone} onChange={(v) => upd((d) => { d.phone = v; })} testId="admin-phone" />
              <Field label="Teléfono (enlace tel:)" value={draft.phone_href} onChange={(v) => upd((d) => { d.phone_href = v; })} testId="admin-phone-href" />
              <Field label="WhatsApp (solo números, ej. 18605787591)" value={draft.whatsapp} onChange={(v) => upd((d) => { d.whatsapp = v; })} testId="admin-whatsapp" />
              <Field label="Email" value={draft.email} onChange={(v) => upd((d) => { d.email = v; })} testId="admin-email" />
            </div>
            <ImageField label="Logo del negocio" value={draft.logo} onChange={(v) => upd((d) => { d.logo = v; })} testId="admin-logo-image" />
          </div>
        )}

        {tab === "hero" && (
          <div className="space-y-6 rounded-2xl border border-navy-border bg-navy-surface p-6" data-testid="admin-hero-panel">
            <BiField label="Texto superior (kicker)" value={draft.hero.kicker} onChange={(v) => upd((d) => { d.hero.kicker = v; })} testId="admin-hero-kicker" />
            <div>
              <p className="mb-2 font-mono text-xs uppercase tracking-widest text-slate-400">Líneas del titular</p>
              <div className="space-y-4">
                {draft.hero.lines.map((line, i) => (
                  <div key={i} className="rounded-xl border border-navy-border p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-mono text-xs text-volt">Línea {i + 1}</span>
                      <label className="flex items-center gap-2 text-xs text-slate-400">
                        <input
                          type="checkbox"
                          data-testid={`admin-hero-line-${i}-accent`}
                          checked={!!line.accent}
                          onChange={(e) => upd((d) => { d.hero.lines[i].accent = e.target.checked; })}
                          className="accent-[#FFD400]"
                        />
                        Amarillo
                      </label>
                    </div>
                    <BiField label="Texto" value={line} onChange={(v) => upd((d) => { d.hero.lines[i] = { ...d.hero.lines[i], ...v }; })} testId={`admin-hero-line-${i}`} />
                  </div>
                ))}
              </div>
            </div>
            <BiField label="Subtítulo" value={draft.hero.subtitle} onChange={(v) => upd((d) => { d.hero.subtitle = v; })} textarea testId="admin-hero-subtitle" />
            <div>
              <div className="mb-3 flex items-center justify-between">
                <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Fotos del hero</p>
                <button
                  type="button"
                  data-testid="admin-hero-add-image"
                  onClick={() => upd((d) => { d.hero.images.push(""); })}
                  className="flex items-center gap-1 rounded-lg bg-navy-border px-3 py-1.5 text-xs font-semibold hover:bg-volt hover:text-navy"
                >
                  <Plus size={14} /> Añadir foto
                </button>
              </div>
              <div className="space-y-4">
                {draft.hero.images.map((url, i) => (
                  <div key={i} className="relative rounded-xl border border-navy-border p-4">
                    <button
                      type="button"
                      data-testid={`admin-hero-image-${i}-remove`}
                      onClick={() => upd((d) => { d.hero.images.splice(i, 1); })}
                      className="absolute right-3 top-3 text-red-400 hover:text-red-300"
                      aria-label="Eliminar"
                    >
                      <Trash2 size={15} />
                    </button>
                    <ImageField label={`Foto ${i + 1}`} value={url} onChange={(v) => upd((d) => { d.hero.images[i] = v; })} testId={`admin-hero-image-${i}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "services" && (
          <div className="space-y-5" data-testid="admin-services-panel">
            {draft.services.map((s, i) => (
              <div key={s.id} className="space-y-4 rounded-2xl border border-navy-border bg-navy-surface p-6">
                <p className="font-mono text-xs font-semibold tracking-widest text-volt">Servicio {i + 1} · {s.id}</p>
                <BiField label="Título" value={s.title} onChange={(v) => upd((d) => { d.services[i].title = v; })} testId={`admin-service-${i}-title`} />
                <BiField label="Descripción" value={s.desc} onChange={(v) => upd((d) => { d.services[i].desc = v; })} textarea testId={`admin-service-${i}-desc`} />
                <ImageField label="Imagen" value={s.image} onChange={(v) => upd((d) => { d.services[i].image = v; })} testId={`admin-service-${i}-image`} />
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <div className="rounded-2xl border border-navy-border bg-navy-surface p-6" data-testid="admin-gallery-panel">
            <p className="mb-4 text-sm text-slate-400">Sube fotos nuevas o elimina las existentes. Recuerda presionar Guardar.</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {draft.gallery.map((url, i) => (
                <div key={`${url}-${i}`} className="group relative overflow-hidden rounded-xl border border-navy-border" data-testid={`admin-gallery-item-${i}`}>
                  <img src={url} alt="" className="aspect-square w-full object-cover" />
                  <button
                    type="button"
                    data-testid={`admin-gallery-remove-${i}`}
                    onClick={() => upd((d) => { d.gallery.splice(i, 1); })}
                    className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white opacity-90 transition-opacity hover:opacity-100"
                    aria-label="Eliminar foto"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <div className="rounded-xl border-2 border-dashed border-navy-border p-2">
                <ImageField
                  label="Añadir foto"
                  value=""
                  onChange={(v) => { if (v) upd((d) => { d.gallery.push(v); }); }}
                  testId="admin-gallery-add"
                />
              </div>
            </div>
          </div>
        )}

        {tab === "testimonials" && (
          <div className="space-y-5" data-testid="admin-testimonials-panel">
            {draft.testimonials.map((item, i) => (
              <div key={i} className="relative space-y-4 rounded-2xl border border-navy-border bg-navy-surface p-6">
                <button
                  type="button"
                  data-testid={`admin-testimonial-remove-${i}`}
                  onClick={() => upd((d) => { d.testimonials.splice(i, 1); })}
                  className="absolute right-4 top-4 text-red-400 hover:text-red-300"
                  aria-label="Eliminar testimonio"
                >
                  <Trash2 size={16} />
                </button>
                <p className="font-mono text-xs font-semibold tracking-widest text-volt">Testimonio {i + 1}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nombre" value={item.name} onChange={(v) => upd((d) => { d.testimonials[i].name = v; })} testId={`admin-testimonial-${i}-name`} />
                </div>
                <BiField label="Cargo / Ciudad" value={item.role} onChange={(v) => upd((d) => { d.testimonials[i].role = v; })} testId={`admin-testimonial-${i}-role`} />
                <BiField label="Comentario" value={item.text} onChange={(v) => upd((d) => { d.testimonials[i].text = v; })} textarea testId={`admin-testimonial-${i}-text`} />
              </div>
            ))}
            <button
              type="button"
              data-testid="admin-testimonial-add"
              onClick={() => upd((d) => { d.testimonials.push({ name: "", role: { es: "", en: "" }, text: { es: "", en: "" }, rating: 5 }); })}
              className="flex items-center gap-2 rounded-xl border-2 border-dashed border-navy-border px-5 py-3 text-sm font-semibold text-slate-300 hover:border-volt hover:text-volt"
            >
              <Plus size={16} /> Añadir testimonio
            </button>
          </div>
        )}

        {tab === "contact" && (
          <div className="space-y-5 rounded-2xl border border-navy-border bg-navy-surface p-6" data-testid="admin-contact-panel">
            <BiField label="Ubicación" value={draft.contact.location} onChange={(v) => upd((d) => { d.contact.location = v; })} testId="admin-contact-location" />
            <BiField label="Horario" value={draft.contact.hours} onChange={(v) => upd((d) => { d.contact.hours = v; })} testId="admin-contact-hours" />
            <BiField label="Ciudades que sirven" value={draft.contact.cities} onChange={(v) => upd((d) => { d.contact.cities = v; })} textarea testId="admin-contact-cities" />
          </div>
        )}
      </main>
    </div>
  );
}
